import { useCallback, useMemo, useRef, useState } from "react";

import type { Observation } from "@medplum/fhirtypes";

import { writeLaborAmendPendingWorkToSession } from "@/encounters/components/EncounterLabor/utils/labor-amend-session-state";

import type { LaborAmendPendingDisplayEntry } from "./types";

interface UseLaborAmendPendingPreviewsParams {
  encounterId: string;
}

/**
 * Owns pending-change banner entries and optimistic observation preview patches during labor amend.
 */
export function useLaborAmendPendingPreviews({ encounterId }: UseLaborAmendPendingPreviewsParams) {
  const pendingEntryLaborStageRef = useRef<Record<string, string>>({});
  const [pendingDisplayEntries, setPendingDisplayEntries] = useState<
    LaborAmendPendingDisplayEntry[]
  >([]);
  const [observationPreviewPatches, setObservationPreviewPatches] = useState<
    Record<string, Partial<Observation>>
  >({});

  const clearLocalPreviews = useCallback(() => {
    setPendingDisplayEntries([]);
    setObservationPreviewPatches({});
    pendingEntryLaborStageRef.current = {};
  }, []);

  const registerPendingDisplayEntry = useCallback((entry: LaborAmendPendingDisplayEntry) => {
    if (entry.laborStageLoincCode) {
      pendingEntryLaborStageRef.current[entry.key] = entry.laborStageLoincCode;
    }
    setPendingDisplayEntries((previous) => {
      const rest = previous.filter((item) => item.key !== entry.key);
      return [...rest, entry];
    });
  }, []);

  const getPendingEntryLaborStageLoincCode = useCallback(
    (key: string) => pendingEntryLaborStageRef.current[key],
    [],
  );

  const setObservationPreviewPatch = useCallback(
    (observationId: string, patch: Partial<Observation>) => {
      writeLaborAmendPendingWorkToSession(encounterId);
      setObservationPreviewPatches((previous) => ({
        ...previous,
        [observationId]: { ...previous[observationId], ...patch },
      }));
    },
    [encounterId],
  );

  const getObservationPreviewPatch = useCallback(
    (observationId: string) => observationPreviewPatches[observationId],
    [observationPreviewPatches],
  );

  const applyObservationAmendPreview = useCallback(
    (observation: Observation): Observation => {
      const { id } = observation;
      if (!id) {
        return observation;
      }
      const patch = observationPreviewPatches[id];
      if (!patch) {
        return observation;
      }
      return { ...observation, ...patch };
    },
    [observationPreviewPatches],
  );

  const hasLocalAmendPreviews =
    pendingDisplayEntries.length > 0 || Object.keys(observationPreviewPatches).length > 0;

  return useMemo(
    () => ({
      pendingDisplayEntries,
      registerPendingDisplayEntry,
      getPendingEntryLaborStageLoincCode,
      setObservationPreviewPatch,
      getObservationPreviewPatch,
      applyObservationAmendPreview,
      hasLocalAmendPreviews,
      clearLocalPreviews,
    }),
    [
      pendingDisplayEntries,
      registerPendingDisplayEntry,
      getPendingEntryLaborStageLoincCode,
      setObservationPreviewPatch,
      getObservationPreviewPatch,
      applyObservationAmendPreview,
      hasLocalAmendPreviews,
      clearLocalPreviews,
    ],
  );
}

export type LaborAmendPendingPreviewsState = ReturnType<typeof useLaborAmendPendingPreviews>;
